<ng-template #brandHead>
  <header class="register-brand">
    @if ($branding.logoMenu(); as logo) {
      <img class="register-brand__logo" [src]="logo" alt="" />
    }
    @if ($branding.displayName(); as brandName) {
      <p class="register-brand__name">{{ brandName }}</p>
    }
    <h1 class="register-title">Registro en el área de propietarios</h1>
  </header>
</ng-template>

<ng-template #stepsIndicator>
  <ng-container [ngTemplateOutlet]="brandHead" />
  <p class="register-progress" aria-live="polite">Paso {{ wizardStep() }} de 3</p>
  <ol class="register-steps" aria-label="Pasos del registro">
    @for (step of steps; track step.id) {
      <li
        [attr.aria-current]="wizardStep() === step.id ? 'step' : null"
        [class.is-active]="wizardStep() === step.id"
        [class.is-done]="wizardStep() > step.id"
      >
        <span class="register-steps__index" aria-hidden="true"></span>
        <span class="register-steps__label">{{ step.label }}</span>
      </li>
    }
  </ol>
</ng-template>

@if (registerStep() === 'success') {
  <section class="register-narrow">
    <div class="register-card">
      <p-card>
        <ng-container [ngTemplateOutlet]="brandHead" />
        <h2 class="register-step-title">Registro completado</h2>
        <p-message severity="success" styleClass="mb-3 w-100">
          Hemos activado el acceso. Recibirás un correo en
          <strong>{{ registeredEmail() }}</strong
          >.
        </p-message>
        <p class="register-lead">
          Si la cuenta es nueva, la primera vez que accedas a la Oficina Virtual deberás cambiar la
          contraseña.
        </p>
        <p-button
          type="button"
          label="Ir a inicio de sesión"
          (onClick)="toLogin()"
          class="w-100"
          [style]="{ width: '100%' }"
        />
      </p-card>
    </div>
  </section>
} @else if (registerStep() === 'loading') {
  <section class="register-narrow">
    <div class="register-card">
      <p-card>
        <ng-container [ngTemplateOutlet]="brandHead" />
        <div
          class="register-loading d-flex flex-column align-items-center py-5"
          aria-busy="true"
          aria-live="polite"
        >
          <p-progressSpinner strokeWidth="4" ariaLabel="Registrando" />
          <p class="mt-3 mb-0 text-secondary">Registrando...</p>
        </div>
      </p-card>
    </div>
  </section>
} @else {
  <section class="register-narrow">
    <div class="register-card">
      <p-card>
        <ng-container [ngTemplateOutlet]="stepsIndicator" />
        <form [formGroup]="form" class="form-container" (ngSubmit)="submitCurrentStep()">
          @if (wizardStep() === 1) {
            <h2 class="register-step-title">Tu email</h2>
            <p class="register-lead">
              Lo usaremos para crear tu acceso en el área de propietarios. Debes tener acceso a él
              porque recibirás un email al completar el proceso.
            </p>
            <p-floatlabel variant="in">
              <p-iconfield>
                <p-inputicon class="pi pi-envelope" />
                <input
                  pInputText
                  type="email"
                  id="email"
                  class="w-100"
                  formControlName="email"
                  autocomplete="email"
                />
              </p-iconfield>
              <label for="email">Email</label>
            </p-floatlabel>
            @if (form.get('email')?.invalid && form.get('email')?.touched) {
              <small class="text-danger d-block mt-2">Introduce un email válido.</small>
            }

            <div class="d-flex align-items-center my-3">
              <p-checkbox
                inputId="register-terms"
                [binary]="true"
                class="me-2"
                [formControl]="checkConditions"
              />
              <label for="register-terms" class="mb-0">
                Acepto los términos, condiciones y
                <a [href]="$branding.privacyPolicyUrl()" target="_blank" rel="noopener noreferrer"
                  >política de privacidad</a
                >
              </label>
            </div>
            @if (checkConditions.touched && checkConditions.value !== true) {
              <small class="text-danger d-block mb-3"
                >Debes aceptar los términos para continuar.</small
              >
            }

            <p-button
              type="submit"
              label="Continuar"
              class="w-100"
              [style]="{ width: '100%' }"
              [disabled]="form.get('email')?.invalid || checkConditions.value !== true"
            />
            <div class="d-flex justify-content-center mt-3">
              <p-button severity="secondary" link (onClick)="toLogin()"
                >Volver a inicio de sesión</p-button
              >
            </div>
          } @else if (wizardStep() === 2) {
            <h2 class="register-step-title">Teléfono móvil</h2>
            <p class="register-lead">
              Para poder enviarte comunicaciones sobre tus consumos o gestiones con
              {{ $branding.displayName() }}.
            </p>
            <p-floatlabel variant="in">
              <p-iconfield>
                <p-inputicon class="pi pi-phone" />
                <input
                  pInputText
                  type="tel"
                  id="phone"
                  class="w-100"
                  formControlName="phone"
                  autocomplete="tel"
                  inputmode="numeric"
                  maxlength="15"
                />
              </p-iconfield>
              <label for="phone">Teléfono móvil</label>
            </p-floatlabel>
            @if (form.get('phone')?.invalid && form.get('phone')?.touched) {
              <small class="text-danger d-block mt-2">
                Introduce un móvil válido de 9 dígitos (debe empezar por 6, 7 o 9).
              </small>
            }

            @if ($branding.isGGM() && phoneHasValue()) {
              <div class="d-flex align-items-center my-3">
                <p-checkbox
                  inputId="register-phone-terms"
                  [binary]="true"
                  class="me-2"
                  [formControl]="checkPhoneConditions"
                  (onChange)="onPhoneConditionsChange($event.checked)"
                />
                <label for="register-phone-terms" class="mb-0">
                  Acepto las condiciones de comunicaciones telemáticas
                </label>
              </div>
              @if (checkPhoneConditions.touched && checkPhoneConditions.value !== true) {
                <small class="text-danger d-block mb-3">
                  Acepta las condiciones para continuar con este teléfono.
                </small>
              }
            }

            <p-button
              type="submit"
              label="Continuar"
              class="w-100 mt-3"
              [style]="{ width: '100%' }"
              [disabled]="!canContinueWithPhone()"
            />
            <div class="d-flex justify-content-center mt-2">
              <p-button
                type="button"
                label="Continuar sin teléfono"
                severity="secondary"
                [text]="true"
                size="small"
                (onClick)="skipPhone()"
              />
            </div>
            <div class="d-flex justify-content-center">
              <p-button
                type="button"
                label="Atrás"
                severity="secondary"
                [text]="true"
                (onClick)="back()"
              />
            </div>
          } @else {
            <h2 class="register-step-title">Datos de tu liquidación</h2>
            <p class="register-lead">
              Introduce el código y el importe de tu última liquidación para verificar tu identidad.
            </p>
            <p-floatlabel class="d-block register-id-field" variant="in">
              <p-iconfield>
                <p-inputicon>
                  <span class="register-field-mark" aria-hidden="true">abc123</span>
                </p-inputicon>
                <input
                  pInputText
                  type="text"
                  id="liquidationCode"
                  class="w-100"
                  formControlName="liquidationCode"
                  autocomplete="off"
                />
              </p-iconfield>
              <label for="liquidationCode">Código de liquidación</label>
            </p-floatlabel>

            <p-floatlabel class="my-3 d-block register-id-field" variant="in">
              <p-iconfield>
                <p-inputicon class="pi pi-euro" />
                <input
                  pInputText
                  type="text"
                  id="liquidationAmount"
                  class="w-100"
                  formControlName="liquidationAmount"
                  autocomplete="off"
                  inputmode="decimal"
                />
              </p-iconfield>
              <label for="liquidationAmount">Importe de la liquidación</label>
            </p-floatlabel>

            <div class="d-flex justify-content-center mb-3">
              <p-button
                type="button"
                label="¿Dónde encuentro estos datos?"
                severity="secondary"
                link
                (onClick)="showLiquidationHelp.set(true)"
              />
            </div>

            @if (msgError) {
              <p-message styleClass="mb-3 w-100" severity="error">{{ msgError }}</p-message>
            }

            <p-button
              type="submit"
              label="Registrarme"
              class="w-100"
              [style]="{ width: '100%' }"
              [disabled]="
                form.get('liquidationCode')?.invalid || form.get('liquidationAmount')?.invalid
              "
            />
            <div class="d-flex justify-content-center mt-2">
              <p-button
                type="button"
                label="Atrás"
                severity="secondary"
                [text]="true"
                (onClick)="back()"
              />
            </div>
          }
        </form>
      </p-card>
    </div>
  </section>
}

<p-dialog
  header="Protección de Datos"
  [visible]="showPhonePrivacyDialog()"
  (visibleChange)="showPhonePrivacyDialog.set($event)"
  [modal]="true"
  [closable]="false"
  [draggable]="false"
  [breakpoints]="{ '960px': '75vw', '576px': '95vw' }"
  [style]="{ width: '50vw' }"
>
  <div class="privacy-content register-privacy">
    <h2 class="register-privacy__title">
      Información básica sobre el tratamiento del número de teléfono móvil
    </h2>
    <p class="d-flex flex-column text-justify text-sm text-secondary my-3">
      <strong class="title">Responsable del tratamiento:</strong> Gomez Group Metering SLU<br />
      <strong class="title mt-3">Finalidad:</strong> Gestionar la relación contractual e informar
      vía SMS o WhatsApp Business.<br />
      <strong class="title mt-3">Legitimación:</strong> Ejecución del contrato.<br />
      <strong class="title mt-3">Destinatarios:</strong
      ><span
        >Sólo se cederán a las empresas del Grupo para ejecución del contrato y exclusivamente a
        terceras partes por obligación legal. El uso de WhatsApp implica una comunicación a través
        de dicha plataforma conforme a sus propias políticas de privacidad que se pueden consultar
        en la
        <a
          href="https://www.whatsapp.com/legal/business-app-privacy-policy?lang=es"
          target="_blank"
          rel="noopener noreferrer"
          >política de privacidad de WhatsApp</a
        >.</span
      ><br />
      <strong class="title mt-3">Derechos:</strong>
      <span
        >El cliente puede ejercer los derechos de acceso, rectificación, supresión, oposición,
        limitación del tratamiento y portabilidad, así como solicitar información adicional sobre el
        tratamiento de sus datos a través de dpo@gomezgroupmetering.com o en la C/ Llodio, 3 CP
        28034 Madrid.</span
      >
      <strong class="title d-flex flex-row mt-3">Información adicional:</strong>
      <span
        >Puede consultar la información ampliada sobre protección de datos en:
        <a href="https://www.gomezgroupmetering.com" target="_blank" rel="noopener noreferrer"
          >www.gomezgroupmetering.com</a
        >.</span
      >
    </p>
  </div>
  <ng-template pTemplate="footer">
    <p-button
      label="Cancelar"
      [text]="true"
      severity="secondary"
      (onClick)="cancelPhonePrivacy()"
    />
    <p-button label="Aceptar" severity="success" (onClick)="acceptPhonePrivacy()" />
  </ng-template>
</p-dialog>

<p-dialog
  header="¿Dónde encuentro estos datos?"
  [visible]="showLiquidationHelp()"
  (visibleChange)="showLiquidationHelp.set($event)"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [style]="{ width: 'min(40rem, 92vw)' }"
>
  <div class="register-help">
    <p class="register-help__lead">
      El código y el importe aparecen en tus liquidaciones de consumo.
    </p>
    <h2 class="register-help__caption">Ejemplos de liquidaciones de consumo</h2>
    <ul class="register-help__list">
      @for (src of $branding.registrationImages(); track src; let index = $index) {
        <li>
          <p-image
            [src]="src"
            [alt]="'Ejemplo ' + (index + 1) + ' de liquidación de consumo'"
            [preview]="true"
            appendTo="body"
          />
        </li>
      }
    </ul>
  </div>
</p-dialog>
